// src/utils/cryptoUtil.js
// Browser-side AES-256-GCM encryption/decryption compatible with Node backend.

// IMPORTANT:
// - REACT_APP_ENCRYPTION_KEY must be a 64-hex-character string (32 bytes).
// - Backend expects: Buffer.concat([iv, encrypted, authTag]) and then base64.
// - Web Crypto returns ciphertext with authTag appended, so iv + ciphertext is exactly iv + encrypted + authTag.

const ALG_NAME = "AES-GCM";
const IV_LENGTH = 12; // 96-bit nonce for AES-GCM
const TAG_LENGTH = 128; // bits

const KEY_HEX = process.env.REACT_APP_ENCRYPTION_KEY;

// Utility: convert hex string to Uint8Array
function hexToUint8Array(hex) {
  if (!hex) throw new Error("Missing encryption key (REACT_APP_ENCRYPTION_KEY).");
  const cleaned = hex.trim();
  if (!/^[0-9a-fA-F]+$/.test(cleaned) || cleaned.length !== 64) {
    throw new Error("REACT_APP_ENCRYPTION_KEY must be 64 hex characters (32 bytes).");
  }
  const arr = new Uint8Array(cleaned.length / 2);
  for (let i = 0; i < cleaned.length; i += 2) {
    arr[i / 2] = parseInt(cleaned.substr(i, 2), 16);
  }
  return arr;
}

// Import raw key to Web Crypto
async function importKey() {
  const raw = hexToUint8Array(KEY_HEX);
  // subtle.importKey accepts ArrayBuffer or TypedArray
  return window.crypto.subtle.importKey("raw", raw, { name: ALG_NAME }, false, ["encrypt", "decrypt"]);
}

// Small helper to base64-encode a Uint8Array safely
function uint8ArrayToBase64(u8) {
  // convert to binary string in chunks to avoid stack/argument limits
  const chunkSize = 0x8000;
  let result = "";
  for (let i = 0; i < u8.length; i += chunkSize) {
    const chunk = u8.subarray(i, i + chunkSize);
    result += String.fromCharCode.apply(null, chunk);
  }
  return btoa(result);
}

// base64 -> Uint8Array
function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encrypts a UTF-8 string and returns a base64 string.
 * The resulting base64 is: base64(iv || ciphertext || authTag)
 */
export async function encrypt(plainText) {
  if (typeof plainText !== "string") {
    throw new Error("encrypt expects a string input");
  }

  const key = await importKey();
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const encoded = new TextEncoder().encode(plainText);

  // Use AES-GCM with explicit tagLength
  const cipherBuffer = await window.crypto.subtle.encrypt({ name: ALG_NAME, iv, tagLength: TAG_LENGTH }, key, encoded);

  const cipherUint8 = new Uint8Array(cipherBuffer);

  // Concatenate iv + cipher (cipher already contains auth tag appended)
  const out = new Uint8Array(iv.length + cipherUint8.length);
  out.set(iv, 0);
  out.set(cipherUint8, iv.length);

  return uint8ArrayToBase64(out);
}

/**
 * Decrypts a base64 string previously produced by `encrypt`.
 * Accepts base64(iv || ciphertext || authTag) and returns UTF-8 string.
 */
export async function decrypt(base64String) {
  if (!base64String) return "";

  const combined = base64ToUint8Array(base64String);

  if (combined.length < IV_LENGTH + 16) {
    // 16 bytes minimum auth tag + at least 1 byte ciphertext
    throw new Error("Invalid encrypted data.");
  }

  const iv = combined.slice(0, IV_LENGTH);
  const ciphertext = combined.slice(IV_LENGTH); // includes auth tag appended at end

  const key = await importKey();

  const plainBuffer = await window.crypto.subtle.decrypt({ name: ALG_NAME, iv, tagLength: TAG_LENGTH }, key, ciphertext);

  return new TextDecoder().decode(plainBuffer);
}
